import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import type { ClonotypeDefinitionEnum } from './generated/generatedOpenAPI';
import { ClonotypeDefinition } from './store/slices/preprocessing.slice';

/**
 * Parses a UTC date string from the backend (format: "2026-02-15 09:05:35.271047")
 * and returns a Date object, treating the input as UTC time.
 *
 * @param dateString - Date string in format "YYYY-MM-DD HH:MM:SS.mmmmmm" (UTC), or a
 *   date-only "YYYY-MM-DD"
 * @returns Date object representing the UTC time
 */
export function parseUTCDateString(dateString: string): Date {
  // Convert backend format to ISO 8601 UTC format by replacing space with 'T' and adding 'Z'.
  // A value with no time of day gets an explicit midnight, because "YYYY-MM-DDZ" is not
  // valid ISO 8601.
  const withTime = dateString.includes(':') ? dateString.replace(' ', 'T') : `${dateString}T00:00:00`;
  return new Date(`${withTime}Z`);
}

/**
 * Checks if a string is in ISO 8601 format (contains 'T' separator)
 */
function isISO8601(dateString: string): boolean {
  return dateString.includes('T');
}

/**
 * Checks if an ISO 8601 string has timezone information
 */
function hasTimezone(dateString: string): boolean {
  return dateString.includes('Z') || /[+-]\d{2}:\d{2}$/.test(dateString);
}

/**
 * Formats a date in the user's current timezone.
 * Automatically handles backend UTC date strings (format: "2026-02-15 09:05:35.271047" or "2026-02-15T09:05:35.271047").
 *
 * @param date - Date object, ISO 8601 string, or backend UTC date string
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @returns Formatted date string in the user's timezone
 */
export const formatDate = (date: Date | string, options?: Intl.DateTimeFormatOptions) => {
  let dateObject: Date;
  let isDateOnly = false;

  if (typeof date === 'string') {
    const isISO = isISO8601(date);

    if (isISO) {
      // If it's ISO format but has no timezone, it's UTC from backend - add 'Z'
      if (!hasTimezone(date)) {
        dateObject = new Date(`${date}Z`);
      } else {
        dateObject = new Date(date);
      }
    } else {
      // If it's not ISO 8601 format, it's the backend space-separated format - parse it as UTC
      isDateOnly = !date.includes(':');
      dateObject = parseUTCDateString(date);
    }
  } else {
    dateObject = date;
  }

  if (!(dateObject instanceof Date) || Number.isNaN(dateObject.getTime())) {
    return '';
  }

  const defaultTimeZone = isDateOnly ? 'UTC' : Intl.DateTimeFormat().resolvedOptions().timeZone;
  const optionsWithTimeZone = {
    ...options,
    timeZone: options?.timeZone ?? defaultTimeZone,
  };
  return new Intl.DateTimeFormat(undefined, optionsWithTimeZone).format(dateObject);
};

export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export function sortByProperty<T>(list: T[], property: keyof T, order: SortDirection = SortDirection.ASC): T[] {
  return Array.from(list).sort((a, b) => {
    const valA = a[property];
    const valB = b[property];

    const isDateString = typeof valA === 'string' && typeof valB === 'string' && !Number.isNaN(Date.parse(valA)) && !Number.isNaN(Date.parse(valB));
    const isDate = valA instanceof Date && valB instanceof Date;
    if (isDateString || isDate) {
      const dateA = new Date(valA);
      const dateB = new Date(valB);
      return order === SortDirection.ASC ? dateB.getTime() - dateA.getTime() : dateA.getTime() - dateB.getTime();
    }

    if (valA < valB) {
      return order === SortDirection.ASC ? -1 : 1;
    }
    if (valA > valB) {
      return order === SortDirection.ASC ? 1 : -1;
    }
    return 0;
  });
}

export function capitalizeFirstLetter(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const customNaturalSort = (a: any, b: any) => {
  return Number(a[0].match(/(\d+)/)[0]) - Number(b[0].match(/(\d+)/)[0]);
};

function parseRange(str: string) {
  if (str === '0') {
    return 0;
  }
  if (str === '1') {
    return 1;
  }
  const [min, max] = str.split('-');

  if (max === undefined) {
    return parseInt(min, 10);
  }
  return (parseInt(min, 10) + parseInt(max, 10)) / 2;
}

export function customRangeSort(a: any, b: any) {
  if (b.name === 'without') {
    return -1;
  }
  if (a.name === 'without') {
    return 1;
  }

  const aNum = parseRange(a.name);
  const bNum = parseRange(b.name);
  return aNum - bNum;
}

export const getRangeForScatterPlot = (scatterData: Array<any>, axisKeyName: string, scaleOption: string = 'normal') => {
  let rangeStart: number = scatterData[0][axisKeyName];
  let rangeEnd: number = scatterData[0][axisKeyName];
  for (const item of scatterData) {
    if (item[axisKeyName] > rangeEnd) {
      rangeEnd = item[axisKeyName];
    }
    if (item[axisKeyName] < rangeStart) {
      rangeStart = item[axisKeyName];
    }
  }
  if (scaleOption === 'log') {
    rangeStart = 0;
    rangeEnd += (rangeEnd / 100) * 38;
    return [Math.log10(rangeStart), Math.log10(rangeEnd)];
  }
  if (rangeEnd < 0) {
    rangeEnd -= (rangeEnd / 100) * 3;
  } else {
    rangeEnd += (rangeEnd / 100) * 3;
  }
  if (rangeStart < 0) {
    rangeStart += (rangeStart / 100) * 3;
  } else {
    rangeStart -= (rangeStart / 100) * 3;
  }
  return [rangeStart, rangeEnd];
};

export const groupArrayOfObjects = (values: Array<any>, groupField: string) => {
  return values.reduce((reducer: any, item: any) => {
    reducer[item[groupField]] = reducer[item[groupField]] || [];
    reducer[item[groupField]].push(item);
    return reducer;
  }, {});
};

export function camelToSnakeCase(str: string) {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

export function snakeToCamelCase(str: string) {
  return str.replace(/([-_][a-z])/g, (group) => group.toUpperCase().replace('-', '').replace('_', ''));
}

export function snakeToTitleCase(str: string) {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getClonotypeEnumFromDefinition(definition: ClonotypeDefinition) {
  // TODO: This function could be removed and replaced with direct enum usage since it's just a type assertion
  // Currently kept for semantic clarity since its used in 16 locations
  return definition as ClonotypeDefinitionEnum;
}

/**
 * Type guard for RTK Query's FetchBaseQueryError.
 */
export function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === 'object' && error != null && 'status' in error;
}

/**
 * Extracts a human-readable error message from RTK Query errors, FastAPI responses, or standard Errors.
 *
 * Handles:
 * - FetchBaseQueryError with FastAPI's `{ detail: string }` response shape
 * - SerializedError with `message`
 * - Standard Error instances
 * - Falls back to `fallback` when the error shape is unrecognized
 */
export function getErrorMessage(error: unknown, fallback = 'An unexpected error occurred'): string {
  if (isFetchBaseQueryError(error)) {
    const { data } = error;
    if (typeof data === 'object' && data != null && 'detail' in data) {
      const { detail } = data as { detail: unknown };
      if (typeof detail === 'string') {
        return detail;
      }
    }
    if (typeof data === 'string') {
      return data;
    }
    return fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  // SerializedError shape from RTK Query
  if (typeof error === 'object' && error != null && 'message' in error) {
    const { message } = error as { message: unknown };
    if (typeof message === 'string') {
      return message;
    }
  }

  return fallback;
}

export function toPercentages(vals: number[]): number[] {
  const total = vals.reduce((a, b) => a + b, 0);
  if (total === 0) {
    return vals.map(() => 0);
  }
  // round percentage to 2 decimal places
  return vals.map((v) => Math.round((v / total) * 10000) / 100);
}
